import { useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useModal } from "@/components/shell/useModal";
import { Button } from "./controls";
import { Glyph } from "./Glyph";
import styles from "./dialog.module.css";

/**
 * A modal dialog for a short task: a title, a sentence about it, the form, and its actions.
 * Focus moves in and returns on close; Escape and the scrim close it.
 */
export function Dialog({
  title,
  lead,
  children,
  footer,
  footNote,
  onClose,
  wide = false,
}: {
  title: string;
  lead?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  footNote?: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const id = useId();
  useModal(box, onClose);
  return createPortal(
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        className={`${styles.panel} ${wide ? styles.wide : ""}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.head}>
          <div className={styles.headText}>
            <h2 id={`${id}-title`} className={styles.title}>
              {title}
            </h2>
            {lead ? <p className={styles.lead}>{lead}</p> : null}
          </div>
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
            <Glyph name="x" size={16} strokeWidth={2.2} />
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer ? (
          <div className={styles.foot}>
            {footNote ? <span className={styles.footNote}>{footNote}</span> : null}
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/** A link or token shown once, with a button that copies it. */
export function CopyField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className={styles.copy}>
      <span className={styles.copyText} title={value}>
        <span className="visually-hidden">{label}: </span>
        {value}
      </span>
      <Button
        size="sm"
        variant="field"
        className={styles.copyButton}
        onClick={() => void copy()}
        aria-label={`Copy ${label.toLowerCase()}`}
        icon={<Glyph name={copied ? "check" : "link"} size={13} strokeWidth={2.2} />}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}
